import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { NavigationSettingAri } from '@atlassian/ari/navigation';
import { IdentityUserAri } from '@atlassian/ari/identity/user';
import { JiraWorkspaceAri } from '@atlassian/ari/jira/workspace';
import { JiraNavigationSettingsAri } from '@atlassian/ari/jira/navigation-settings';
import { fg } from '@atlassian/jira-feature-gating';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import GSNQuery, {
	type GSNQueryPreloaderQuery,
} from './__generated__/GSNQueryPreloaderQuery.graphql.ts';

// Result matches the generics of the async component's EntryPointProps
// except for TComponentProps which is derived from `root`
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
type EntryPointParams = EntryPointRouteParams & any;

type GSNSidebarContentEntryPointComponent = EntryPointComponent<
	// gsnSidebarContent is optional: for anonymous users, no query is preloaded (empty queries object)
	{
		gsnSidebarContent?: GSNQueryPreloaderQuery;
	},
	{},
	{},
	{ isShortcutsStatic?: boolean } | null
>;

export const gsnSidebarContentEntryPoint: EntryPoint<
	GSNSidebarContentEntryPointComponent,
	EntryPointParams
> = createEntryPoint({
	// sidebar is critical to rendering the initial view of a page
	root: JSResourceForUserVisible<GSNSidebarContentEntryPointComponent>(
		/* webpackChunkName: "gsn-sidebar-content-entrypoint" */ './GSNForContentSidebarEntryPoint.tsx',
		{ includeInShellOnlySsrBundle: true, entryFsSsrLiftImportToModule: true },
	),

	getPreloadProps(params: EntryPointParams) {
		/**
		 * IMPORTANT: The query variables should be the same as in other queries, in order to avoid multiple
		 *   network requests.
		 */
		const {
			tenantContext: { activationId, atlassianAccountId, cloudId, isAnonymous },
			context,
			entryPoints = {},
			isAppsExpanded,
			isFiltersExpanded,
			isPlansExpanded,
			isProjectsExpanded,
			isOperationsExpanded,
		} = params;

		if (isAnonymous) {
			// Suppress all network requests for anonymous users
			return { queries: {}, entryPoints };
		}

		// Fetch the productionisation recommendations as part of the GSN query (via
		// `growthRecommendations @include(if: $shouldFetchInvitedAppShortcuts)`) for
		// authed users in the test cohort. Read the experiment via
		// `UNSAFE_noExposureExp` so control (`isEnabled: false`) skips the field
		// WITHOUT firing an exposure here — the exposure fires in
		// `GSNForContentSidebarWithInvitedAppRecommendations.tsx` once recs render.
		const shouldFetchInvitedAppShortcuts =
			fg('twcg-460-preload-grs-invited-app-shortcuts-gate') &&
			UNSAFE_noExposureExp('twcg-460-preload-grs-invited-app-shortcuts')[0].get('isEnabled', false);

		const entityAriUserSideNavSettings = JiraWorkspaceAri.create({
			siteId: cloudId,
			activationId,
		}).toString();
		const navPlatMenuDataEntityAri = NavigationSettingAri.create({
			activationId,
			firstPartyApp: 'jira',
			siteId: cloudId,
			settingId: 'nav-plat-l1-menu-data',
		}).toString();
		const navPlatSidebarDataEntityAri = NavigationSettingAri.create({
			activationId,
			firstPartyApp: 'jira',
			siteId: cloudId,
			settingId: 'nav-plat-sidebar-data',
		}).toString();

		function entityAri(scope: string) {
			return JiraNavigationSettingsAri.create({
				siteId: cloudId,
				activationId,
				navigationSettingScope: scope,
			}).toString();
		}

		const ownerAri =
			atlassianAccountId && IdentityUserAri.create({ userId: atlassianAccountId }).toString();

		return {
			// Queries your async component uses
			queries: {
				gsnSidebarContent: {
					// generated .graphql
					parameters: GSNQuery,
					// variables for your query
					variables: {
						cloudId,
						...(fg('sea-4270-current-url-in-gsn')
							? {
									currentURL: new URL(
										`http://example.com${context.match.url}/?${new URLSearchParams(
											context.query ?? {},
										).toString()}`,
									).toString(),
								}
							: {}),
						entityAriApps: entityAri('apps'),
						entityAriFilters: entityAri('filters'),
						entityAriPlans: entityAri('plans'),
						...(fg('jpd-enable-roadmaps-recents-fe')
							? { entityAriRoadmaps: entityAri('collections') }
							: {}),
						entityAriUserSideNavSettings,
						isAppsExpanded,
						isFiltersExpanded,
						isPlansExpanded,
						isProjectsExpanded,
						isOperationsExpanded,
						navPlatMenuDataEntityAri,
						navPlatSidebarDataEntityAri,
						ownerAri,
						isRoadmapUpsellEnabled: fg('jpd-roadmaps-in-jira'),
						fetchGlobalShortcutsAvailableProducts: Boolean(
							cloudId && ownerAri && fg('navx-4515-jira-gsn-gas-ssr'),
						),
						isItamEnabled: fg('itam_ftw'),
						itamStaticChecks: fg('itam_ftw')
							? [
									{
										resourceId: `ari:cloud:jira-servicedesk::site/${cloudId}`,
										permissionId: 'write',
									},
									{
										resourceId: `ari:cloud:jira::site/${cloudId}`,
										permissionId: 'manage',
									},
									{
										resourceId: `ari:cloud:cmdb::site/${cloudId}`,
										permissionId: 'manage',
									},
								]
							: [],
						shouldFetchInvitedAppShortcuts,
						invitedAppShortcutsProduct: 'jira',
					},
					// fetch options
					options: {
						fetchPolicy: 'store-or-network' as const,
					},
				},
			},
			entryPoints,
		};
	},
});
